import {
  Tooltip
} from "@fluentui/react-components";
import { ListChevronsDownUp, ListChevronsUpDown } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import DropDown from "../../components/controls/dropdown/DropdownFluent";
import EntityFileUpload from "../../components/controls/fileUpload/EntityFileUpload";
import Input from "../../components/controls/input/customInput/CustomInput";
import InputUnderline from "../../components/controls/input/InputUnderline";
import ListingHeader from "../../components/controls/listingHeader/ListingHeader";
import Modal from "../../components/controls/modal/Modal";
import ModalCheckbox from "../../components/controls/modalcheckbox/ModalCheckbox";
import ModalCheckboxWarning from "../../components/controls/modalcheckbox/ModalCheckboxWarning";
import { ColumnConfigProvider } from "../../components/controls/tables/ColumnConfigProvider";
import Table from "../../components/controls/tables/Table";
import { TABLE_ACTIONS } from "../../components/controls/tables/utils/tableUtils";
import DateRangeWithDuration from "../../components/dateRangeWithDuration/DateRangeWithDuration";
import OverviewHeader from "../../components/overviewHeader/OverviewHeader";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import TaskDependencies from "../../components/taskDependencies/TaskDependencies";
import TaskResourceAllocationManager from "../../components/taskResourceAllocation/TaskResourceAllocationManager";
import {
  calculatePageSize,
  getLabelByType,
  taskPriorityOptions,
  taskStatusOptions
} from "../../helpers/utils";
import { CalendarIcon } from "../../modules/default-svg";
import { generalActions } from "../../store/actions";
import scopeService from "../../store/services/scope.service";
import taskGroupService from "../../store/services/taskGroup.service";
import {
  dateOnlyFormatted,
  isDateGreaterThan,
  weeksBetweenDates
} from "../../utils/systemSettings";
import KanbanBoard from "../kanbanBoard";
import AssociatedRisks from "../risk/AssociatedRisks";
import { AlertTriangle, XCircle, X } from "lucide-react";
import SafeHtml from "../../components/safeHtml/SafeHtml";
import CreateEditTaskGroup from "../scopeOverview/CreateEditTaskGroup";
import {
  nextWorkingDayAfter,
  calendarDaysDiff,
  shiftDateStr,
} from "../scopeOverview/utils";
import useNavigation from "../../utils/useNavigation";

const TaskGroupOverview = (props) => {
  const { taskGroupID } = props;
  const settings = JSON.parse(localStorage.getItem("settings"));
  const { navigateTo } = useNavigation();
  const dispatch = useDispatch();

  const [groupLoading, setGroupLoading] = useState(false);
  const [headerTitle, setHeaderTitle] = useState([]);
  const [overviewHeaderFields, setOverviewHeaderFields] = useState([]);
  const [scope, setScope] = useState({});
  const [project, setProject] = useState({});
  const [progressSummary, setProgressSummary] = useState(null);

  const [isTaskLoading, setIsTaskLoading] = useState(false);
  const [isTaskActionCompleted, setIsTaskActionCompleted] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [perPageItems] = useState(calculatePageSize());
  const [pageNum, setPageNum] = useState(1);
  const [data, setData] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [kanbanView, setKanbanView] = useState(false);

  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [completionDate, setCompletionDate] = useState("");
  const [status, setStatus] = useState(0);
  const [priority, setPriority] = useState(0);
  const [phase, setPhase] = useState("");
  const [refId, setRefId] = useState("");
  const [description, setDescription] = useState("");
  const [isDraft, setIsDraft] = useState(false);
  const [isCritical, setIsCritical] = useState(false);
  const [isTaskMilestone, setIsTaskMilestone] = useState(false);
  const [isInWsr, setIsInWsr] = useState(false);
  const [taskDependencies, setTaskDependencies] = useState([]);
  const [taskDependenciesDto, setTaskDependenciesDto] = useState([]);
  const [taskAllocations, setTaskAllocations] = useState([]);
  const [taskAttachments, setTaskAttachments] = useState([]);
  const [taskProgress, setTaskProgress] = useState(0);
  const [taskRisks, setTaskRisks] = useState([]);
  const [taskDateConflicts, setTaskDateConflicts] = useState([]);
  const [formErrors, setFormErrors] = useState({
    name: "",
    startDate: "",
    endDate: "",
    completionDate: "",
  });

  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(localStorage.getItem("isOverviewHeaderCollapsed") === "true");

  const handleHeaderSectionState = () => {
    setIsHeaderCollapsed(!isHeaderCollapsed);
    localStorage.setItem("isOverviewHeaderCollapsed", !isHeaderCollapsed);
  };

  const handleListingView = () => setKanbanView(false);
  const handleKanbanView = () => setKanbanView(true);

  const fetchTaskGroupProgressSummary = () => {
    taskGroupService
      .getTaskGroupProgressSummaryRequest({ id: taskGroupID })
      .then((data) => setProgressSummary(data))
      .catch(() => { });
  };

  const fetchTaskGroupData = () => {
    setGroupLoading(true);
    fetchTaskGroupProgressSummary();
    taskGroupService
      .getTaskGroupDetailsRequest({ id: taskGroupID })
      .then((groupData) => {
        setScope(groupData.scope || {});
        setProject(groupData.project || {});
        setHeaderTitle({ name: groupData.name });
        const res = [
          {
            ...groupData.creator,
            label: "Created By",
            isResource: true,
          },
          {
            label: "Scope",
            name: groupData.scope?.name || <>&nbsp;</>,
          },
          {
            label: "Start / End date",
            name: groupData.startDate || groupData.endDate
              ? `${groupData.startDate ? dateOnlyFormatted(settings, groupData.startDate) : "-"} to ${groupData.endDate ? dateOnlyFormatted(settings, groupData.endDate) : "-"}`
              : "-",
            secondary: groupData.startDate && groupData.endDate
              ? weeksBetweenDates(groupData.startDate, groupData.endDate)
              : null,
          },
        ];
        setOverviewHeaderFields(res);
      })
      .catch((error) => {
        console.error("Error fetching task group data:", error);
      })
      .finally(() => {
        setGroupLoading(false);
      });
  };

  const fetchGroupTasks = () => {
    if (!scope?.id || !taskGroupID) return;
    setPageLoading(true);
    taskGroupService
      .getTaskGroupTasksRequest({ scopeId: scope.id, taskGroupId: taskGroupID })
      .then((tasks) => setData(tasks || []))
      .catch(() => setData([]))
      .finally(() => setPageLoading(false));
  };

  useEffect(() => {
    fetchTaskGroupData();
  }, [taskGroupID]);

  useEffect(() => {
    if (scope?.id) fetchGroupTasks();
  }, [scope?.id, taskGroupID]);

  const handleSearch = (filteredData) => {
    setData(filteredData);
  };

  const fetchTaskData = async (taskId) => {
    setIsTaskLoading(true);
    scopeService
      .getTaskDetails({ id: taskId ? taskId : selectedRow.id })
      .then((taskData) => {
        setName(taskData.name);
        setStartDate(taskData.startDate);
        setEndDate(taskData.endDate);
        setCompletionDate(taskData.completionDate);
        setPriority(taskData.priority);
        setPhase(taskData.phase);
        setTaskProgress(taskData.progress);
        setTaskRisks(taskData.risks);
        setStatus(taskData.status);
        setRefId(taskData.refId);
        setDescription(taskData.description);
        setIsCritical(taskData.isCritical);
        setIsTaskMilestone(taskData.isMilestone);
        setIsDraft(taskData.isDraft);
        setIsInWsr(taskData.isInWsr);
        setTaskDependencies(taskData.dependencies);
        setTaskAllocations(taskData.allocations || []);
        setTaskAttachments(taskData.attachments || []);
        setTaskDateConflicts([]);
        setIsTaskLoading(false);
      })
      .catch(() => { });
  };

  const handleCreate = () => {
    setIsCreate(true);
    setStartDate("");
    setEndDate("");
    setIsButtonLoading(false);
    setTaskProgress(0);
    setTaskRisks([]);
  };

  useEffect(() => {
    if (Object.keys(selectedRow).length > 0) {
      isEdit && fetchTaskData();
    } else {
      setName("");
      setStartDate("");
      setEndDate("");
      setCompletionDate("");
      setStatus(0);
      setPhase("");
      setRefId("");
      setTaskProgress(0);
      setTaskRisks([]);
      setDescription("");
      setIsCritical(false);
      setIsTaskMilestone(false);
      setIsDraft(false);
      setIsInWsr(false);
      setPriority(0);
      setTaskDependencies([]);
      setTaskAttachments([]);
      setTaskAllocations([]);
      setTaskDateConflicts([]);
      setIsTaskActionCompleted(true);
    }
  }, [selectedRow, isEdit]);

  useEffect(() => {
    if (name) {
      setFormErrors((prevErrors) => ({ ...prevErrors, name: "" }));
    }
  }, [name]);

  useEffect(() => {
    if (startDate && endDate && isDateGreaterThan(startDate, endDate)) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        endDate: `End date must be equal to or greater than start date.`,
      }));
    } else {
      setFormErrors((prevErrors) => ({ ...prevErrors, endDate: "" }));
    }
  }, [endDate]);

  useEffect(() => {
    if (startDate && completionDate && isDateGreaterThan(startDate, completionDate)) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        completionDate: `Completion date must be equal to or greater than start date.`,
      }));
    } else {
      setFormErrors((prevErrors) => ({ ...prevErrors, completionDate: "" }));
    }
  }, [completionDate]);

  const handleAllocationsChange = (allocations) => {
    setTaskAllocations(allocations);

    const dates = allocations
      .filter((a) => (a.startDate || a.StartDate) && (a.endDate || a.EndDate))
      .map((a) => ({ start: a.startDate || a.StartDate, end: a.endDate || a.EndDate }));

    if (dates.length > 0) {
      const minStart = dates.reduce((min, d) => (d.start < min ? d.start : min), dates[0].start);
      const maxEnd = dates.reduce((max, d) => (d.end > max ? d.end : max), dates[0].end);
      setStartDate(minStart);
      setEndDate(maxEnd);
    }
  };

  const handleTaskDependenciesChange = (dependencies) => {
    setTaskDependenciesDto(dependencies);

    if (!dependencies?.length) return;

    let requiredStart = null;
    for (const dep of dependencies) {
      let depRequired = null;
      if (dep.dependencyType === 0 && dep.endDate) {
        depRequired = nextWorkingDayAfter(dep.endDate);
      } else if (dep.dependencyType === 1 && dep.startDate) {
        depRequired = dep.startDate.split('T')[0];
      }
      if (depRequired && (!requiredStart || depRequired > requiredStart)) {
        requiredStart = depRequired;
      }
    }

    if (!requiredStart) return;
    if (startDate && requiredStart <= startDate) return;

    const hasProgress = Number(taskProgress) > 0;
    const deltaDays = startDate ? calendarDaysDiff(startDate, requiredStart) : 0;

    if (!hasProgress) {
      setStartDate(requiredStart);
      if (endDate && deltaDays > 0) {
        setEndDate(shiftDateStr(endDate, deltaDays));
      }
    } else {
      if (endDate && deltaDays > 0) {
        setEndDate(shiftDateStr(endDate, deltaDays));
      }
    }

    if (taskAllocations?.length > 0 && deltaDays > 0) {
      const updatedAllocations = taskAllocations.map((a) => {
        const aStart = a.startDate;
        const aEnd = a.endDate;
        if (!aStart && !aEnd) return a;

        if (!hasProgress) {
          return {
            ...a,
            startDate: aStart ? shiftDateStr(aStart, deltaDays) : null,
            endDate: aEnd ? shiftDateStr(aEnd, deltaDays) : null,
            isDirty: true,
          };
        } else {
          if (!aEnd) return a;
          return { ...a, endDate: shiftDateStr(aEnd, deltaDays), isDirty: true };
        }
      });
      setTaskAllocations(updatedAllocations);
    }
  };

  const handleSave = () => {
    const assignedResourcesPayload = (taskAllocations || []).map((a) => ({
      resourceId: a.resource?.id,
      hoursPerDay: a.hoursPerDay,
      startDate: a.startDate,
      endDate: a.endDate,
      isIncludeWeekends: a.isIncludeWeekends,
    }))

    const payload = {
      ...(name && { Name: name }),
      ...(refId && { RefId: refId }),
      ...(startDate && { startDate: startDate }),
      ...(endDate && { endDate: endDate }),
      ...(completionDate && { completionDate: completionDate }),
      ...(description && { Description: description }),
      ...(phase && { Phase: phase }),
      ...(isDraft && { isDraft: isDraft }),
      ...(isCritical && { isCritical: isCritical }),
      ...(isTaskMilestone && { isMilestone: isTaskMilestone }),
      ...(isInWsr && { isInWsr: isInWsr }),
      ScopeId: scope.id,
      TaskGroupId: taskGroupID,
      ProjectId: project.id,
      AssignedResources: assignedResourcesPayload,
      progress: taskProgress || 0,
      Dependencies: taskDependenciesDto,
      status,
      priority,
    };

    const errors = {};
    if (!name) errors["name"] = `The Name field is required.`;
    if (startDate && endDate && isDateGreaterThan(startDate, endDate)) {
      errors["endDate"] = `End date must be equal to or greater than start date.`;
    }
    setFormErrors(errors);
    if (Object.keys(errors).length) {
      return;
    }

    setIsButtonLoading(true);

    if (Object.keys(selectedRow).length > 0) {
      scopeService
        .updateTaskRequest({ id: selectedRow.id, payload })
        .then((response) => {
          setIsButtonLoading(false);
          const result = response?.data ?? response;
          const value = result?.value ?? result;
          
          if (value && value.isPermissible === false) {
            setTaskDateConflicts(value.conflicts || []);
            return;
          }
          setTaskDateConflicts([]);
          dispatch(
            generalActions.showToast({
              title: "Task updated successfully",
              type: "success",
            })
          );
          handleCancel();
          fetchGroupTasks();
          fetchTaskGroupData();
        })
        .catch(() => {
          setIsTaskActionCompleted(true);
          setIsButtonLoading(false)
          dispatch(
            generalActions.showToast({
              title: "Something went wrong. Please try again later.",
              type: "error",
            })
          );
        });
    } else {
      scopeService
        .createTaskRequest(payload)
        .then((response) => {
          setIsButtonLoading(false)
          const result = response?.data ?? response;
          const value = result?.value ?? result;

          if (value && value.isPermissible === false) {
            setTaskDateConflicts(value.conflicts || []);
            return;
          }
          setTaskDateConflicts([]);
          dispatch(
            generalActions.showToast({
              title: "Task created successfully",
              type: "success",
            })
          );
          setIsTaskActionCompleted(true);
          handleReset();
          fetchGroupTasks();
          fetchTaskGroupData();
        })
        .catch(() => {
          setIsTaskActionCompleted(true);
          setIsButtonLoading(false)
          dispatch(
            generalActions.showToast({
              title: "Something went wrong. Please try again later.",
              type: "error",
            })
          );
        });
    }
  };

  const handleDelete = (row) => {
    setIsButtonLoading(true);
    scopeService
      .deleteTaskRequest({ id: row.id })
      .then(() => {
        dispatch(
          generalActions.showToast({ title: "Deleted successfully", type: "success" })
        );
        fetchGroupTasks();
        fetchTaskGroupData();
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => setIsButtonLoading(false));
    setIsTaskActionCompleted(true);
    handleReset();
  };

  const handleTaskActionCallback = () => {
    fetchGroupTasks();
    fetchTaskGroupData();
  };

  const handleDuplicate = (actionProps) => {
    if (actionProps.payload.id) {
      scopeService
        .duplicateTaskRequest({ id: actionProps.payload.id, payload: {} })
        .then(() => {
          dispatch(
            generalActions.showToast({ title: "Task Duplicated successfully", type: "success" })
          );
          handleTaskActionCallback();
        })
        .catch(() => {
          dispatch(
            generalActions.showToast({
              title: "Something went wrong. Please try again later.",
              type: "error",
            })
          );
        });
    }
  };

  const handleTableAction = (actionProps) => {
    switch (actionProps.actionType) {
      case TABLE_ACTIONS.DUPLICATE:
        handleDuplicate(actionProps);
        break;
    }
  };

  const handleReset = () => {
    setFormErrors({});
    setIsDraft(false);
    setIsCritical(false);
    setIsTaskMilestone(false);
    setIsInWsr(false);
    setIsEdit(false);
    setIsCreate(false);
    setTaskDependencies([]);
    setTaskDependenciesDto([]);
    setTaskAllocations([]);
    setTaskProgress(0);
    setTaskRisks([]);
    setTaskAttachments([]);
    setTaskDateConflicts([]);
    setIsTaskActionCompleted(false);
  };

  const handleCancel = () => {
    setIsCreate(false);
    setIsEdit(false);
    setSelectedRow({});
    handleReset();
  };

  const handleAddRow = () => { };
  const handleCancelAddRow = () => fetchGroupTasks();

  const handleDeleteTaskGroup = () => {
    setIsButtonLoading(true);
    taskGroupService
      .deleteTaskGroupRequest({ id: taskGroupID })
      .then(() => {
        dispatch(
          generalActions.showToast({
            type: "success",
            message: "Task group deleted successfully.",
          })
        );
        navigateTo("ScopeItems", scope.id, scope.name);
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            type: "error",
            message: "Error deleting task group.",
          })
        );
      })
      .finally(() => {
        setIsButtonLoading(false);
        setDeleteModal(false);
      });
  };

  const taskFormBody = () => {
    return (
      <>
        <div className="d-flex align-items-end justify-content-between">
          <div className="col-10">
            <InputUnderline
              placeholder="Add task name"
              value={name}
              error={formErrors.name}
              handleChange={setName}
              isRequired
              loading={isTaskLoading}
            />
          </div>
          <div className="w-20 ml-2">
            <Input
              icon="%"
              placeholder="0"
              max={100}
              value={taskProgress}
              type="number"
              handleChange={setTaskProgress}
              label="Progress"
              loading={isTaskLoading}
            />
          </div>
        </div>

        <div className="flex col-10 gap-3 mt-3">
          <ModalCheckboxWarning
            label={isCritical ? "Critical" : "Mark as Critical"}
            checked={isCritical}
            setChecked={setIsCritical}
            loading={isTaskLoading}
          />
          <ModalCheckbox
            label={isTaskMilestone ? "Milestone" : "Mark as Milestone"}
            checked={isTaskMilestone}
            setChecked={setIsTaskMilestone}
            loading={isTaskLoading}
          />
          <ModalCheckbox
            label={isInWsr ? "In WSR" : "Include in WSR"}
            checked={isInWsr}
            setChecked={setIsInWsr}
            loading={isTaskLoading}
          />
        </div>

        <TaskDependencies
          projectId={project.id}
          scope={{ label: scope.name, value: scope.id }}
          taskDependencies={taskDependencies}
          onDependenciesChange={handleTaskDependenciesChange}
          loading={isTaskLoading}
        />

        {isEdit && <AssociatedRisks isLoading={isTaskLoading} risks={taskRisks} />}

        <div className="row align-items-center gx-3 my-3">
          <TaskResourceAllocationManager
            scopeId={scope.id}
            taskId={isEdit ? selectedRow?.id : null}
            allocations={taskAllocations}
            onChange={handleAllocationsChange}
            onTaskDateConflicts={setTaskDateConflicts}
            loading={isTaskLoading}
            buttonLabel="Assign Resources"
            taskStartDate={startDate}
            taskEndDate={endDate}
          />
        </div>

        <div className="row align-items-center gx-3 mb-3">
          <div className="col-12">
            {taskDateConflicts.length > 0 && (() => {
              const hasError = taskDateConflicts.some(c => c.conflictType === "ResourceCapacity");
              const borderColor = hasError ? "#f87171" : "#fb923c";
              const bgColor = hasError ? "#fef2f2" : "#fff7ed";
              const headerColor = hasError ? "#b91c1c" : "#c2410c";
              return (
                <div style={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: "8px",
                  background: bgColor,
                  padding: "10px 14px",
                  marginBottom: "12px",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                    <span style={{ fontWeight: 600, fontSize: "14px", color: headerColor }}>Date conflicts detected</span>
                    <button
                      type="button"
                      onClick={() => setTaskDateConflicts([])}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", color: headerColor }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {Object.entries(
                    taskDateConflicts.reduce((acc, c) => {
                      const key = c.conflictType || "Other";
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(c);
                      return acc;
                    }, {})
                  ).map(([type, items], idx) => (
                    <div key={type} style={idx > 0 ? { marginTop: "8px", paddingTop: "8px", borderTop: "1px solid rgba(0,0,0,0.06)" } : undefined}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 600, fontSize: "13px", color: "#374151", marginBottom: "4px" }}>
                        {type === "ResourceCapacity"
                          ? <XCircle size={16} style={{ color: "#dc2626", flexShrink: 0 }} />
                          : <AlertTriangle size={16} style={{ color: "#ea580c", flexShrink: 0 }} />}
                        <span>{type.replace(/([a-z])([A-Z])/g, "$1 $2")}</span>
                      </div>
                      <ul style={{ margin: 0, padding: "0 0 0 22px", listStyle: "none", display: "flex", flexDirection: "column", gap: "4px" }}>
                        {items.map((c, i) => (
                          <li key={i} style={{ fontSize: "13px", color: "#1a1a1a", lineHeight: 1.4 }}>
                            <SafeHtml html={c.message} className="" style={{ flex: 1 }} />
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
          <DateRangeWithDuration
            isLoading={isTaskLoading}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            startDateError={formErrors.startDate}
            endDateError={formErrors.endDate}
            isStartDateDisabled={Number(taskProgress) > 0}
            icon={<CalendarIcon />}
          />
          <div className="col-4">
            <Input
              icon={<CalendarIcon />}
              value={completionDate}
              minDate={endDate || startDate}
              type="date"
              handleChange={setCompletionDate}
              label="Completion Date"
              errorMessage={formErrors.completionDate}
              loading={isTaskLoading}
            />
          </div>
        </div>

        <div className="row align-items-center gx-3 mb-3">
          <div className="col-3">
            <DropDown
              label="Priority"
              options={taskPriorityOptions}
              defaultVal={getLabelByType("taskPriority", priority)}
              onChange={setPriority}
              loading={isTaskLoading}
            />
          </div>
          <div className="col-3">
            <DropDown
              label="Status"
              options={taskStatusOptions}
              defaultVal={getLabelByType("taskStatus", status)}
              onChange={setStatus}
              loading={isTaskLoading}
            />
          </div>
          <div className="col-3">
            <Input
              icon={false}
              placeholder="Enter phase"
              value={phase}
              type="text"
              handleChange={setPhase}
              label="Phase"
              loading={isTaskLoading}
            />
          </div>
          <div className="col-3">
            <Input
              icon={false}
              placeholder="Enter Change Ticket"
              value={refId}
              type="text"
              handleChange={setRefId}
              label="Change Ticket"
              loading={isTaskLoading}
            />
          </div>
        </div>

        <RichTextEditor
          wrapperClass="mb-3"
          label="Description"
          content={description}
          onChange={setDescription}
          loading={isTaskLoading}
        />

        {isEdit && selectedRow && (
          <EntityFileUpload
            entityId={selectedRow?.id}
            entityType="Task"
            attachments={taskAttachments}
            onAttachmentsChange={async () => {
              if (selectedRow?.id) {
                scopeService
                  .getTaskAttachements({ id: selectedRow?.id })
                  .then((attachements) => {
                    setTaskAttachments(attachements || []);
                  });
              }
            }}
            disabled={isTaskLoading}
          />
        )}
      </>
    );
  };

  return (
    <div className="relative">
      <ColumnConfigProvider tableName="Tasks">
        {
          !isHeaderCollapsed &&
          (
            <OverviewHeader
              entityType="scope"
              isShowAction
              headerTitle={headerTitle}
              headerFields={overviewHeaderFields}
              loading={groupLoading}
              showDeleteModal={() => setDeleteModal(true)}
              showEditModal={() => setEditModal(true)}
              progressSummary={progressSummary}
            />
          )
        }

        <div
          className="TasksScreenWrapper mt-4"
          style={{
            height: `calc(100vh - ${isHeaderCollapsed ? '205' : '400'}px)`,
          }}>
          {kanbanView && (
            <ListingHeader
              type="Tasks"
              parentType="TaskGroup"
              btnLable="Add Task"
              taskGroupId={taskGroupID}
              handleListingView={handleListingView}
              handleKanbanView={handleKanbanView}
              kanbanView={kanbanView}
              searchedData={handleSearch}
              setIsCreate={handleCreate}
              rightPane={true}
              triggerSearch={() => { }}
              isSearchDisable={true}
            />
          )}
          {!kanbanView ? (
            <Table
              type="Tasks"
              rows={data || []}
              searchHandler={handleSearch}
              body={taskFormBody()}
              saveBtnLabel="Save"
              cancelBtnLabel="Cancel"
              setPageNum={setPageNum}
              perPageItems={perPageItems}
              setIsCreate={setIsCreate}
              handleSave={() => handleSave()}
              handleDelete={(row) => handleDelete(row)}
              handleCancel={() => handleCancel()}
              setIsEdit={setIsEdit}
              loading={pageLoading}
              modalLoading={!isTaskActionCompleted}
              isCreate={isCreate}
              pagination={{}}
              setSelectedRow={setSelectedRow}
              scopeId={scope.id}
              taskGroupId={taskGroupID}
              handleAddRow={handleAddRow}
              handleCancelAddRow={handleCancelAddRow}
              handleListingView={handleListingView}
              handleKanbanView={handleKanbanView}
              kanbanView={kanbanView}
              setData={setData}
              parentType="Scope"
              triggerSearch={() => { }}
              handleAction={handleTableAction}
            />
          ) : (
            <KanbanBoard
              scopeID={scope.id}
              data={data}
              type="Tasks"
              isCreate={isCreate}
              setIsCreate={setIsCreate}
              loading={pageLoading}
              modalLoading={!isTaskActionCompleted}
              body={taskFormBody()}
              setSelectedRow={setSelectedRow}
              setEdit={() => setIsEdit(true)}
              isButtonLoading={isButtonLoading}
              saveHandler={handleSave}
              heightDelta={isHeaderCollapsed ? '195px' : '370px'}
              handleAction={handleTableAction}
            />
          )}

          {deleteModal && (
            <Modal
              title="Delete task group"
              body={
                <p className="text-sm-400 subHeading-color">
                  {`Deleting "${headerTitle.name || "this task group"}" will also delete its grouped tasks. Are you sure you want to continue?`}
                </p>
              }
              handleCancel={() => setDeleteModal(false)}
              handleSave={() => handleDeleteTaskGroup()}
              saveBtnLabel="Delete"
              cancelBtnLabel="Cancel"
              isLoading={isButtonLoading}
            />
          )}

          {editModal && (
            <CreateEditTaskGroup
              taskGroup={{ id: taskGroupID }}
              isEdit
              scopeId={scope.id}
              requireScopePicker={false}
              isOpen={editModal}
              onClose={() => setEditModal(false)}
              onSaved={() => fetchTaskGroupData()}
            />
          )}
        </div>
      </ColumnConfigProvider>
      {!groupLoading &&
        (
          <div
            className="absolute right-2 cursor-pointer opacity-50 hover:opacity-100 hover:text-[#342C82] px-3 py-1 transition-all duration-150 ease-in-out"
            style={{ top: isHeaderCollapsed ? '-2rem' : '0.25rem' }}
            onClick={handleHeaderSectionState}>
            <Tooltip content={isHeaderCollapsed ? "Expand" : "Collapse"} relationship="label" positioning="before">
              {isHeaderCollapsed ? <ListChevronsUpDown size={20} /> : <ListChevronsDownUp size={20} />}
            </Tooltip>
          </div>
        )}
    </div>
  );
};
export default TaskGroupOverview;
